// frontend/src/components/Profile/EditAvatarModal.js
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Slider,
  Alert,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AvatarEditor from 'react-avatar-editor';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    maxWidth: 550,
    minWidth: 450,
    maxHeight: '90vh',
    overflow: 'hidden',
    zIndex: 1300,
    backgroundColor: theme.palette.grey[50],
    boxShadow: theme.shadows[0]
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.grey[600],
    },
  }
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EditAvatarModal = ({ open, onClose, onSave, currentAvatarUrl }) => {
  const theme = useTheme();
  const editorRef = useRef(null);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  const handleScaleChange = (event, newValue) => {
    setScale(newValue);
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Проверка типа файла
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Допустимые форматы: JPEG, PNG, GIF, WEBP');
      return;
    }

    // Проверка размера (максимум 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Размер файла не должен превышать 5MB');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!editorRef.current) return;

    setLoading(true);
    setError('');

    try {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
      });

      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

      await onSave(file);

      onClose();
    } catch (err) {
      console.error('❌ Ошибка сохранения аватарки:', err);
      setError(err.message || 'Не удалось сохранить фото');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
    setScale(1.2);
    setError('');
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth={false} fullWidth={false}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PhotoCameraIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="700">
            Редактирование фото
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white', borderRadius: theme.spacing(1.5) }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        {!selectedImage ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                overflow: 'hidden'
              }}
            >
              {currentAvatarUrl ? (
                <img
                  src={currentAvatarUrl}
                  alt="Текущая аватарка"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <PhotoCameraIcon sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.5 }} />
              )}
            </Box>

            <Typography variant="body1" gutterBottom>
              Выберите новое фото для профиля
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Допустимые форматы: JPEG, PNG, GIF, WEBP. Максимальный размер: 5MB
            </Typography>

            <Button
              variant="contained"
              component="label"
              startIcon={<PhotoCameraIcon />}
              sx={{ borderRadius: 5 }}
            >
              Выбрать фото
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageSelect}
              />
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <AvatarEditor
              ref={editorRef}
              image={selectedImage}
              width={280}
              height={280}
              border={50}
              borderRadius={140}
              color={[0, 0, 0, 0.6]}
              scale={scale}
              style={{ cursor: 'move' }}
            />

            <Box sx={{ width: '100%', px: 2 }}>
              <Typography gutterBottom>Масштаб</Typography>
              <Slider
                value={scale}
                min={1}
                max={3}
                step={0.1}
                onChange={handleScaleChange}
                aria-labelledby="scale-slider"
              />
            </Box>

            <Button
              variant="outlined"
              onClick={() => setSelectedImage(null)}
              sx={{ borderRadius: 5 }}
            >
              Выбрать другое фото
            </Button>
          </Box>
        )}
      </StyledDialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: 5, px: 4 }}
        >
          Отмена
        </Button>
        {selectedImage && (
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{ borderRadius: 5, px: 4 }}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default EditAvatarModal;